import React, { useState, useEffect } from "react";
import {
  RadioGroup,
  Radio,
  FormControlLabel,
  Box,
  Typography,
  Grid,
  TextField,
  makeStyles,
} from "@material-ui/core";
import GarnishDialog from "../../GarnishDialog"; 
import QuizQuestion from "./QuizQuestion"; 

const useStyles = makeStyles((theme) => ({
  columnHeader: {
    textTransform: "uppercase",
    color: "#888",
    fontWeight: "bold",
    marginBottom: "1rem",
    fontSize: "0.9rem",
  },
  readOnlyText: {
    background: "#f5f5f5",
    padding: "1rem",
    borderRadius: "4px",
    marginBottom: "1.5rem",
    border: "1px solid #e0e0e0",
    minHeight: "3.5rem",
    whiteSpace: "pre-wrap",
  },
  inputGroup: {
    marginBottom: "1.5rem",
  },
  label: {
    fontWeight: "bold",
    marginBottom: "0.5rem",
    display: "block",
    fontSize: "0.9rem",
  },
  translationHeader: {
    color: "var(--garnish-primary-green)",
  },
}));

export default function EditQuestionModal({
  open,
  onClose,
  question,
  onSave,
  isNewQuestion,
  languageBeingEditedId,
  defaultLanguageId,
}) {
  const classes = useStyles();
  const [step, setStep] = useState(1);
  const [isMajor, setIsMajor] = useState(false);
  const [localQuestion, setLocalQuestion] = useState(null); 
  const [isDirty, setIsDirty] = useState(false);

  const isTranslationMode =
    languageBeingEditedId &&
    defaultLanguageId &&
    languageBeingEditedId !== defaultLanguageId;

  useEffect(() => {
    if (open && question) {
      let initialQuestionState = { ...question };

      if (isTranslationMode) {
        setStep(2);
        setIsMajor(false);
      } else if (isNewQuestion) {
        if (initialQuestionState.questionText === "Untitled Question") {
          initialQuestionState.questionText = "";
        }
        initialQuestionState.answers = initialQuestionState.answers.map(
          (a) => ({
            ...a,
            answerText: a.answerText.startsWith("Option") ? "" : a.answerText,
          })
        );
        setStep(2);
        setIsMajor(true);
      } else {
        setStep(1);
        setIsMajor(false);
      }

      setLocalQuestion(initialQuestionState);
      setIsDirty(false);
    }
  }, [open, question, isNewQuestion, isTranslationMode]);

  const handleSave = () => {
    // --- VALIDATION START ---
    if (!localQuestion.questionText || !localQuestion.questionText.trim()) {
        alert("Please enter the question text.");
        return;
    }

    for (let i = 0; i < localQuestion.answers.length; i++) {
        const answer = localQuestion.answers[i];
        if (!answer.answerText || !answer.answerText.trim()) {
            alert(`Please enter text for Option ${i + 1}.`);
            return;
        }
    }
    // --- VALIDATION END ---

    onSave({ ...localQuestion, isMajorChange: isMajor });
    setIsDirty(false);
    onClose();
  };

  const handleCloseAttempt = () => {
    if (isDirty) {
      const confirmDiscard = window.confirm(
        "You have unsaved changes. Are you sure you want to discard them?"
      );
      if (!confirmDiscard) {
        return;
      }
    }
    setIsDirty(false);
    onClose();
  };

  const handleQuestionChange = (updatedQuestion) => {
    setLocalQuestion(updatedQuestion);
    setIsDirty(true);
  };

  // --- TRANSLATION HELPERS ---
  const getTranslatedText = (obj, field) => {
    if (!obj) return "";
    const translations = obj[`${field}Translations`] || [];
    const entry = translations.find(
      (t) => t.languageId === languageBeingEditedId
    );
    return entry ? entry.translatedValue : "";
  };

  const updateTranslatedText = (obj, field, value) => {
    const translations = [...(obj[`${field}Translations`] || [])];
    const index = translations.findIndex(
      (t) => t.languageId === languageBeingEditedId
    );

    if (index > -1) {
      translations[index] = { ...translations[index], translatedValue: value };
    } else {
      translations.push({
        languageId: languageBeingEditedId,
        translatedValue: value,
      });
    }
    return { ...obj, [`${field}Translations`]: translations };
  };

  const handleTranslationChange = (field, value, idx = null) => {
    let updatedQ = { ...localQuestion };

    if (idx === null) {
      updatedQ = updateTranslatedText(updatedQ, field, value);
    } else {
      const newAnswers = [...updatedQ.answers];
      newAnswers[idx] = updateTranslatedText(newAnswers[idx], field, value);
      updatedQ.answers = newAnswers;
    }

    setLocalQuestion(updatedQ);
    setIsDirty(true);
  };

  const getTitle = () => {
    if (isTranslationMode) return "Translate Question";
    if (isNewQuestion) return "Create New Question";
    return step === 1 ? "Step 1: Edit Strategy" : "Step 2: Modify Content";
  };

  const renderTranslationSplitView = () => (
    <Grid container style={{ height: "100%" }}>
      <Grid item xs={6}>
        <div
          style={{
            paddingRight: "1rem",
            borderRight: "1px solid #eee",
            height: "100%",
            overflowY: "auto",
          }}
        >
          <Typography className={classes.columnHeader}>
            Original (English)
          </Typography>
          <div className={classes.inputGroup}>
            <span className={classes.label}>Question</span>
            <div className={classes.readOnlyText}>
              {localQuestion?.questionText}
            </div>
          </div>
          {localQuestion?.answers?.map((a, i) => (
            <div key={a.id} className={classes.inputGroup}>
              <span className={classes.label}>Option {i + 1}</span>
              <div className={classes.readOnlyText}>{a.answerText}</div>
            </div>
          ))}
        </div>
      </Grid>

      <Grid item xs={6}>
        <div
          style={{ paddingLeft: "1rem", height: "100%", overflowY: "auto" }}
        >
          <Typography
            className={`${classes.columnHeader} ${classes.translationHeader}`}
          >
            Translation
          </Typography>

          <div className={classes.inputGroup}>
            <span className={classes.label}>Question</span>
            <TextField
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={getTranslatedText(localQuestion, "questionText")}
              onChange={(e) =>
                handleTranslationChange("questionText", e.target.value)
              }
              placeholder="Translate question..."
            />
          </div>

          {localQuestion?.answers?.map((a, i) => (
            <div key={a.id} className={classes.inputGroup}>
              <span className={classes.label}>Option {i + 1}</span>
              <TextField
                fullWidth
                variant="outlined"
                value={getTranslatedText(a, "answerText")}
                onChange={(e) =>
                  handleTranslationChange("answerText", e.target.value, i)
                }
                placeholder={`Translate option ${i + 1}...`}
              />
            </div>
          ))}
        </div>
      </Grid>
    </Grid>
  );

  if (!open || !localQuestion) return null;

  return (
    <GarnishDialog
      open={open}
      title={getTitle()}
      onClose={handleCloseAttempt}
      showCancelButton={true}
      onCancel={handleCloseAttempt}
      maxWidth={isTranslationMode ? "xl" : "sm"} 
      confirmText={
        step === 1
          ? "Next"
          : isTranslationMode
          ? "Save Translation"
          : isNewQuestion
          ? "Create Question"
          : "Apply Changes"
      }
      onConfirm={step === 1 ? () => setStep(2) : handleSave}
    >
      {step === 1 && !isTranslationMode ? (
        <Box p={2}>
          <Typography gutterBottom>
            How should this edit be tracked?
          </Typography>
          <RadioGroup
            value={isMajor ? "major" : "minor"}
            onChange={(e) => setIsMajor(e.target.value === "major")}
          >
            <FormControlLabel
              value="minor"
              control={<Radio color="primary" />}
              label="Minor Fix (Typo) - Keep same ID"
            />
            <FormControlLabel
              value="major"
              control={<Radio color="primary" />}
              label="Major Change - New ID & Archive old version"
            />
          </RadioGroup>
        </Box>
      ) : isTranslationMode ? (
        <Box p={2} height="60vh">
           {renderTranslationSplitView()}
        </Box>
      ) : (
        <QuizQuestion
          id={localQuestion.id}
          question={localQuestion}
          onChange={handleQuestionChange}
        />
      )}
    </GarnishDialog>
  );
}